import { useMemoQueriesResults } from '@chronosphereio/shared';
import { QueryObserverOptions, UseQueryResult, useQueries, useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef } from 'react';
import {
  DATA_QUERY_KEY,
  FormulaDefinition,
  MIN_PER_QUERY_SERIES_LIMIT,
  QueryDefinition,
  SupportedDataType,
} from '@/core';
import { useChartsTheme } from '../../../components/context/ChartsProvider';
import { InternalGetDataResponse } from '../../model';
import { useAppMetadataContext } from '../AppMetadataProvider';
import { getQueryOptions, usePluginQueryContext } from '../common';
import { initializeDataQueryPlugin, isProgressiveDataQueryPlugin } from '../DataQueryPlugins';
import { usePluginRegistry, usePlugins } from '../plugin-registry';
import {
  DataPluginQueryMethod,
  DataQueryPagination,
  DEFAULT_DATA_QUERY_MODE,
  FrameworkQueryMeta,
  UseDataQueryOptions,
} from './model';
import { getDataQueryQueryFn } from './queryFns';

export const DEFAULT_PAGE_NUMBER = 0;
export const DEFAULT_PAGE_SIZE = 100;

/**
 * Runs multiple data queries using plugins and returns the results.
 *
 * @deprecated This hook is deprecated for use outside of viz-framework. Instead use
 * `DataQueriesProvider` and `useDataQueryResult` to ensure your use case has consistent features
 * and functionality from the framework.
 */
export function useDeprecatedDataQueries<T extends SupportedDataType>(
  definitions: QueryDefinition[],
  options?: UseDataQueryOptions,
  queryOptions?: QueryObserverOptions,
  id?: string
): Array<UseQueryResult<InternalGetDataResponse<T>>> {
  return useRunDataQueries(definitions, [], options, queryOptions, id);
}

/**
 * @private Should only be used by the viz-framework
 */
export function useRunDataQueries<T extends SupportedDataType>(
  definitions: QueryDefinition[],
  formulas?: FormulaDefinition[],
  options?: UseDataQueryOptions,
  queryOptions?: QueryObserverOptions,
  id?: string
): Array<UseQueryResult<InternalGetDataResponse<T>>> {
  const { getPlugin } = usePluginRegistry();
  const queryClient = useQueryClient();

  // disable pagination if there is more than 1 query to prevent any weird edge cases
  const enablePagination = definitions.length < 2;
  const paginationModel: DataQueryPagination | undefined = enablePagination
    ? {
        page: options?.pagination?.page ?? DEFAULT_PAGE_NUMBER,
        pageSize: options?.pagination?.pageSize ?? DEFAULT_PAGE_SIZE,
      }
    : undefined;

  // keep track of the next cursor associated with each page you fetched.
  const mapPageToNextCursor = useRef<string[]>([]);
  const cursorToFetch = mapPageToNextCursor.current[(paginationModel?.page ?? 0) - 1];

  const queryLimits = options?.queryLimits;
  const effectiveQueryLimits = useMemo(() => {
    if (!queryLimits?.seriesLimit || definitions.length <= 1) {
      return queryLimits;
    }
    const perQueryLimit = Math.min(
      queryLimits.seriesLimit,
      Math.max(MIN_PER_QUERY_SERIES_LIMIT, Math.floor(queryLimits.seriesLimit / definitions.length))
    );
    return { ...queryLimits, seriesLimit: perQueryLimit };
  }, [queryLimits, definitions.length]);

  const context = usePluginQueryContext({
    panelDefinition: options?.panelDefinition,
    dataType: options?.dataType,
    disabledGlobalFilters: options?.disabledGlobalFilters,
    suggestedStepMs: options?.suggestedStepMs,
    resolution: options?.resolution,
    allowCompare: options?.allowCompare,
    queryLimits: effectiveQueryLimits,
  });
  const appMetadata = useAppMetadataContext();
  const chartsTheme = useChartsTheme();

  const pluginLoaderResponse = usePlugins(
    DATA_QUERY_KEY,
    definitions.map((d) => ({ kind: d.spec.plugin.kind }))
  );

  const baseQueryMethod: DataPluginQueryMethod =
    options?.allowCompare === true &&
    context.compareDurationInMs !== undefined &&
    context.compareDurationInMs !== null &&
    context.compareDurationInMs > 0
      ? 'getComparableData'
      : 'getData';

  const queryMode = options?.queryMode || DEFAULT_DATA_QUERY_MODE;

  // Keeps track of whether a plugin is still waiting on its dependencies.
  // Takes the idx of the definition and sets it to true if it's waiting
  const dependenciesLoadingByIdx: Record<number, boolean> = {};
  const queryResults = useMemoQueriesResults(
    useQueries({
      queries: definitions.map((definition, idx) => {
        const plugin = pluginLoaderResponse[idx]?.data;

        // Threshold-flagged queries opt out of compare mode. Gate on the same
        // feature flag as `applyQueryThreshold`
        const thresholdQueryEnabled = context.featureFlags?.enableThresholdQueryOption === true;
        const isThresholdQuery = thresholdQueryEnabled && definition.spec.threshold !== undefined;
        const perQueryContext = isThresholdQuery ? { ...context, compareDurationInMs: undefined } : context;
        const perQueryQueryMethod: DataPluginQueryMethod = isThresholdQuery ? 'getData' : baseQueryMethod;

        const dataQueryPlugin = plugin ? initializeDataQueryPlugin(plugin) : undefined;
        const { queryEnabled, queryKey } = getQueryOptions({
          plugin: dataQueryPlugin,
          definition,
          context: perQueryContext,
          cursor: cursorToFetch,
          id,
        });
        // Set true if it's waiting on dependencies
        dependenciesLoadingByIdx[idx] = queryEnabled === false;

        const meta: FrameworkQueryMeta = {
          dataQueryType: dataQueryPlugin && isProgressiveDataQueryPlugin(dataQueryPlugin) ? 'progressive' : 'basic',
        };

        if (queryMode === 'unmounted') {
          queryClient.removeQueries({
            queryKey: queryKey,
          });
        }

        const shouldRunQuery = (formulas && formulas.length > 0) || !definition.spec.disabled;
        return {
          enabled: (queryOptions?.enabled ?? true) && queryEnabled && shouldRunQuery && queryMode === 'active',
          queryKey: queryKey,
          queryFn: getDataQueryQueryFn<T>({
            appMetadata,
            chartsTheme,
            context: perQueryContext,
            cursorToFetch,
            definition,
            enablePagination,
            getPlugin,
            id: definition.spec.id ?? `${idx}`,
            mapPageToNextCursor,
            options,
            paginationModel,
            queryClient,
            queryMethod: perQueryQueryMethod,
          }),
          // Because we have a unique query key that changes every time we ask to refetch the data, we can set a
          // staleTime of infinity which will indefinitely use the result of the query.  This is helpful, for example, when
          // opening the "edit panel" drawer which will reuse this query.  By allowing the stale data, it will not fire off a
          // new request
          staleTime: Infinity,
          cacheTime: 0, // Default of 5 min is too high and unnecessary due to dynamic nature of timeseries data
          meta,
        };
      }),
    })
  );
  return useMemo(() => {
    return queryResults.map((result, idx) => {
      // Since dependencies are considered to be loading, we need to set isLoading to true
      // See https://docs.google.com/document/d/1VNub2wtBC0uQmk_-FC34172xm9q_M9PpUkg9ej2TBOs/edit#heading=h.scoce1dme0fq
      if (dependenciesLoadingByIdx[idx] === true) {
        result.isLoading = true;
        result.isInitialLoading = true;
      }

      return result;
    });
    // Query results is the only thing that should cause this to re-run
    // since its the source of truth for the query results
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryResults]);
}
